import audioUrl from './assets/battle-event-bgm.mp3?url';

/* ==========================================================================
   Background music

   Played through the Web Audio API rather than an <audio loop> element.

   The track is only ~31s, so it wraps roughly twice a minute. MP3 carries
   encoder delay and end padding, which means <audio loop> inserts a short
   silent gap at every wrap — audible on a track this short. An
   AudioBufferSourceNode loops the decoded samples exactly, so the wrap is
   genuinely seamless and needs no fade to disguise it.

   Fades use exponentialRampToValueAtTime on a GainNode. Perceived loudness is
   roughly logarithmic, so an exponential ramp sounds like an even fade where a
   linear one lurches.
   ========================================================================== */

const BGM_STORAGE_KEY = 'bgmEnabled';

const FADE_IN_S = 1.6;
const FADE_OUT_S = 0.8;

// exponentialRampToValueAtTime cannot touch zero, so silence is approximated.
const SILENT = 0.0001;

let ctx = null;
let gainNode = null;
let buffer = null;
let bufferPromise = null;
let source = null;

let playing = false;
let gestureHandler = null;

// Playback position is carried across a stop so toggling back on resumes
// roughly where it left off instead of restarting the track.
let startedAt = 0;
let offset = 0;
let stopTimer = null;

// Music is on by default; the preference survives a refresh.
function isBgmEnabled() {
  try {
    return localStorage.getItem(BGM_STORAGE_KEY) !== 'off';
  } catch (e) {
    // Private mode / storage disabled — fall back to the default.
    return true;
  }
}

function setBgmEnabled(on) {
  try {
    localStorage.setItem(BGM_STORAGE_KEY, on ? 'on' : 'off');
  } catch (e) {
    console.warn('Unable to persist BGM preference', e);
  }
}

function updatePlayButton(on) {
  const button = document.getElementById('playButton');
  if (button) button.textContent = on ? 'MUSIC ON' : 'MUSIC OFF';
}

/* --- graph --------------------------------------------------------------- */

async function ensureGraph() {
  if (!ctx) {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    ctx = new AudioCtx();

    gainNode = ctx.createGain();
    gainNode.gain.value = SILENT;
    gainNode.connect(ctx.destination);
  }

  if (!buffer) {
    if (!bufferPromise) {
      bufferPromise = fetch(audioUrl)
        .then((res) => res.arrayBuffer())
        .then((data) => ctx.decodeAudioData(data));
    }
    buffer = await bufferPromise;
  }
}

// Chrome leaves resume() pending indefinitely while the autoplay policy still
// blocks the page, so it is raced against a short timeout and the real answer
// is read off ctx.state. Never hangs.
async function resumeContext() {
  if (ctx.state === 'running') return true;

  const resumed = ctx.resume().then(
    () => true,
    () => false
  );
  await Promise.race([resumed, new Promise((r) => setTimeout(r, 300))]);

  return ctx.state === 'running';
}

function rampTo(value, seconds) {
  const now = ctx.currentTime;
  const gain = gainNode.gain;

  gain.cancelScheduledValues(now);
  gain.setValueAtTime(Math.max(gain.value, SILENT), now);
  gain.exponentialRampToValueAtTime(Math.max(value, SILENT), now + seconds);
}

/* --- controls ------------------------------------------------------------ */

function setupAudio() {
  // The button reflects the preference, not whether the browser has allowed
  // playback yet — otherwise it reads "MUSIC OFF" on load even though music is
  // enabled and about to start.
  updatePlayButton(isBgmEnabled());

  if (isBgmEnabled()) {
    // Arm the gesture fallback before attempting playback, so a blocked start
    // is always recoverable on the first interaction.
    armFirstGesture();
    startAudio().catch(() => {
      // Blocked by the autoplay policy — the gesture fallback covers it.
    });
  }
}

// Start playback on the first interaction anywhere on the page (autoplay
// fallback). Stays armed until a start actually succeeds.
function armFirstGesture() {
  if (gestureHandler) return;

  gestureHandler = () => {
    if (!isBgmEnabled()) {
      disarmFirstGesture();
      return;
    }

    startAudio()
      .then(disarmFirstGesture)
      .catch((e) => console.warn('Audio still blocked:', e));
  };

  window.addEventListener('pointerdown', gestureHandler);
  window.addEventListener('keydown', gestureHandler);
  window.addEventListener('touchstart', gestureHandler);
}

function disarmFirstGesture() {
  if (!gestureHandler) return;

  window.removeEventListener('pointerdown', gestureHandler);
  window.removeEventListener('keydown', gestureHandler);
  window.removeEventListener('touchstart', gestureHandler);
  gestureHandler = null;
}

// Fades in. Rejects if the browser still blocks audio.
async function startAudio() {
  await ensureGraph();

  if (!(await resumeContext())) {
    throw new Error('AudioContext blocked by the autoplay policy');
  }

  // Cancel a fade-out still in flight and reuse the running source, so
  // off-then-on mid-fade rides back up instead of restarting.
  if (stopTimer !== null) {
    clearTimeout(stopTimer);
    stopTimer = null;
  }

  if (!source) {
    source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true; // sample-accurate: no MP3 seam
    source.connect(gainNode);
    source.start(0, offset % buffer.duration);
    startedAt = ctx.currentTime - offset;
  }

  rampTo(1, FADE_IN_S);

  updatePlayButton(true);
  playing = true;
}

// Fades out, then tears the source down once silent.
function stopAudio() {
  updatePlayButton(false);
  playing = false;

  if (!ctx || !source || stopTimer !== null) return;

  rampTo(SILENT, FADE_OUT_S);

  stopTimer = setTimeout(() => {
    offset = (ctx.currentTime - startedAt) % buffer.duration;

    try {
      source.stop();
    } catch (e) {
      // already stopped
    }
    source.disconnect();
    source = null;
    stopTimer = null;
  }, FADE_OUT_S * 1000 + 60);
}

// Driven by the stored preference rather than the `playing` flag, so the button
// always does what its label says even while autoplay is still blocked.
function toggleAudio() {
  if (isBgmEnabled()) {
    setBgmEnabled(false);
    disarmFirstGesture();
    stopAudio();
  } else {
    setBgmEnabled(true);
    updatePlayButton(true);
    // The click itself is a gesture, so this normally succeeds outright.
    startAudio()
      .then(disarmFirstGesture)
      .catch((e) => console.warn('Audio still blocked:', e));
  }
}

document.getElementById('playButton')?.addEventListener('click', toggleAudio);

setupAudio();
